import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  StandardFormInput,
  StandardFormSelect,
} from "@/components/common/StandardFormInput";
import {
  Headphones,
  MessageCircle,
  Send,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { createTicket } from "@/services/ticketService";
import {
  CreateTicketDto,
  TicketType,
  TicketPriority,
} from "@/types/models/ticket";
import { useAppSelector } from "@/store/hooks";
import { toast } from "sonner";

interface SupportPopoverProps {
  children: React.ReactNode; // The button that triggers the popover
}

interface SupportFormData {
  title: string;
  description: string;
  type: TicketType;
  priority: TicketPriority;
}

export const SupportPopover: React.FC<SupportPopoverProps> = ({ children }) => {
  const params = useParams();
  const navigate = useNavigate();
  const { adminAuth, userId } = useAppSelector((state) => state.auth);
  const storeType = params.storeType as string | undefined;
  const storeId = params.storeId as string | undefined;

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState<string | null>(null);
  const triggerChild = React.isValidElement(children) ? children : <span>{children}</span>;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
  } = useForm<SupportFormData>({
    mode: "onChange",
    defaultValues: {
      type: "general",
      priority: "normal",
    },
  });

  const watchedType = watch("type");

  // Ticket type options
  const ticketTypeOptions = [
    { label: "Technical Issue", value: "technical" },
    { label: "General Support", value: "general" },
    { label: "Website Issue", value: "website" },
    { label: "Product Issue", value: "products" },
    { label: "Order Problem", value: "orders" },
    { label: "Payments / Billing", value: "payments" },
    { label: "Shipping Issue", value: "shipping" },
    { label: "Account Issue", value: "account" },
    { label: "Other", value: "other" },
  ];

  // Priority options
  const priorityOptions = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "normal" },
    { label: "High", value: "high" },
    { label: "Urgent", value: "urgent" },
  ];

  // Quick templates based on ticket type
  const getQuickTemplate = (type: TicketType): string => {
    const templates: Record<TicketType, string> = {
      technical: "I&apos;m experiencing a technical issue with...",
      general: "I need help with...",
      website: "I need help with my website...",
      products: "I&apos;m having an issue with my products...",
      orders: "I need assistance with an order...",
      payments: "I have a question about my billing...",
      shipping: "I have a shipping-related question...",
      billing: "I have a question about my billing...",
      account: "I need help with my account...",
      other: "I need help with something else...",
    };
    return templates[type] || templates.general;
  };

  // Update description when type changes
  const handleTypeChange = (newType: string) => {
    setValue("type", newType as TicketType);
    if (!watch("description")) {
      setValue("description", getQuickTemplate(newType as TicketType));
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    const colors = {
      low: "bg-green-500/20 text-green-600",
      normal: "bg-blue-500/20 text-blue-600",
      high: "bg-orange-500/20 text-orange-600",
      urgent: "bg-red-500/20 text-red-600",
    };
    return colors[priority];
  };

  const onSubmit = async (data: SupportFormData) => {
    setIsSubmitting(true);

    try {
      const createdById = adminAuth?.userId || userId || storeId || "";
      const ticketData: CreateTicketDto = {
        title: data.title,
        subject: data.title,
        description: data.description,
        type: data.type,
        priority: data.priority,
        createdById,
        createdByType: "seller",
        participants: [
          {
            userId: createdById,
            userType: "seller",
            role: "requester",
            visible: true,
          },
        ],
      };

      const response = await createTicket(ticketData);

      // Handle the response - it might be wrapped in data property or direct
      const ticketId =
        (response as any)?.data?.id ||
        (response as any)?.id ||
        ((response as any)?.data
          ? Object.keys((response as any).data)[0]
          : null);

      setCreatedTicketId(ticketId);
      setSubmitSuccess(true);

      toast.success("Support ticket created successfully!");

      // Reset form
      reset();
    } catch (error: any) {
      console.error("Failed to create ticket:", error);
      toast.error(error?.message || "Failed to create support ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitSuccess(false);
    setCreatedTicketId(null);
    reset();
    setIsOpen(false);
  };

  const handleViewTicket = () => {
    if (createdTicketId && storeType && storeId) {
      setIsOpen(false);
      navigate(
        `/${storeType}/${storeId}/tickets/${createdTicketId}`,
      );
    }
  };

  const handleViewAllTickets = () => {
    if (storeType && storeId) {
      setIsOpen(false);
      navigate(`/${storeType}/${storeId}/tickets`);
    }
  };

  if (submitSuccess) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>{triggerChild}</PopoverTrigger>
        <PopoverContent
          className="w-80 p-0"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h3 className="font-semibold">Ticket Created!</h3>
            </div>

            <div className="space-y-4">
              <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium text-green-800 dark:text-green-200 text-sm">
                    Support Request Submitted
                  </span>
                </div>
                {createdTicketId && (
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Ticket ID:{" "}
                    <code className="bg-green-100 dark:bg-green-900/50 px-1 py-0.5 rounded text-xs">
                      {createdTicketId}
                    </code>
                  </p>
                )}
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  We&apos;ll get back to you soon!
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {createdTicketId && (
                  <Button
                    size="sm"
                    onClick={handleViewTicket}
                    className="flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View Ticket
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleViewAllTickets}
                  className="flex items-center gap-1"
                >
                  <MessageCircle className="h-3 w-3" />
                  All Tickets
                </Button>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{triggerChild}</PopoverTrigger>
      <PopoverContent
        className="w-96 p-0"
        align="end"
        side="bottom"
        sideOffset={8}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Headphones className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold">Contact Support</h3>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Ticket Type Selection */}
            <div>
              <StandardFormSelect
                label="Type"
                value={watchedType}
                onValueChange={handleTypeChange}
                options={ticketTypeOptions}
                error={errors.type?.message}
              />
            </div>

            {/* Priority Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <div className="grid grid-cols-2 gap-1">
                {priorityOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setValue("priority", option.value as TicketPriority)
                    }
                    className={`p-2 text-left border rounded text-sm hover:bg-muted/50 transition-colors ${
                      watch("priority") === option.value
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{option.label}</span>
                      <Badge
                        className={`text-xs ${getPriorityColor(option.value as TicketPriority)}`}
                      >
                        {option.value}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <StandardFormInput
                label="Subject"
                placeholder="Brief description"
                required
                {...register("title", {
                  required: "Subject is required",
                  minLength: {
                    value: 5,
                    message: "Subject must be at least 5 characters",
                  },
                })}
                error={errors.title?.message}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder={getQuickTemplate(watchedType)}
                className="min-h-[100px] resize-none text-sm"
                {...register("description", {
                  required: "Description is required",
                  minLength: {
                    value: 20,
                    message: "Description must be at least 20 characters",
                  },
                })}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!isValid || isSubmitting}
                className="flex items-center gap-2"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {isSubmitting ? "Creating..." : "Submit"}
              </Button>
            </div>
          </form>
        </div>
      </PopoverContent>
    </Popover>
  );
};
